/**
 * ZoomControls component - Dropdown chevron, zoom icon button, and percentage display
 * Factory function following the app's architectural patterns
 */

import { createIconButton, type IconButtonAPI } from "@UI/IconButton/index.js";
import {
  createZoomModal,
  type ZoomModalAPI,
} from "./subcomponents/ZoomModal/index.js";
import { getZoomModeIcon } from "@utils/zoom-actions.js";
import { createChevron, type ChevronAPI } from "@UI/Chevron/index.js";
import type { UIStateAPI, ZoomMode } from "@/types/user-types.js";
import "./ZoomControls.scss";

export interface ZoomControlsConfig {
  readonly uiState: UIStateAPI;
  readonly onZoomAction?: (mode: ZoomMode) => void;
}

export interface ZoomControlsAPI {
  readonly getElement: () => HTMLElement;
  readonly updateFromState: () => void;
  readonly destroy: () => void;
}

/**
 * Creates ZoomControls component with dropdown, zoom button, and percentage
 * @param config - Configuration for the zoom controls
 * @returns ZoomControls API
 */
export const createZoomControls = (
  config: ZoomControlsConfig
): ZoomControlsAPI => {
  const { uiState, onZoomAction } = config;

  let isDestroyed = false;
  let zoomButtonAPI: IconButtonAPI | null = null;
  let zoomPercentageElement: HTMLElement | null = null;
  let zoomModalAPI: ZoomModalAPI | null = null;
  let dropdownChevronElement: HTMLElement | null = null;
  let dropdownChevronAPI: ChevronAPI | null = null;

  // Create container element
  const container = document.createElement("div");
  container.className = "zoom-controls";

  /**
   * Handle dropdown chevron click
   */
  const handleDropdownClick = (): void => {
    console.log("🎯 ZoomControls: Chevron clicked", {
      isDestroyed,
      hasModalAPI: !!zoomModalAPI,
      hasDropdownElement: !!dropdownChevronElement,
    });

    if (isDestroyed) return;

    // Close existing modal if open
    if (zoomModalAPI) {
      console.log("🎯 ZoomControls: Closing existing modal");
      zoomModalAPI.destroy();
      zoomModalAPI = null;
      return;
    }

    // Create and show zoom modal
    if (dropdownChevronElement) {
      const state = uiState.getState();
      console.log("🎯 ZoomControls: Creating zoom modal with state:", {
        currentMode: state.zoom_mode,
        hasSelection: state.current_selection.length > 0,
        selectionCount: state.current_selection.length,
      });

      try {
        zoomModalAPI = createZoomModal({
          anchorElement: dropdownChevronElement,
          currentMode: state.zoom_mode,
          hasSelection: state.current_selection.length > 0,
          onSelect: (mode: ZoomMode) => {
            console.log("🎯 ZoomControls: Mode selected:", mode);
            handleZoomModeSelect(mode);
            // The modal will be closed by the ModalManager automatically
          },
          onClose: () => {
            console.log("🎯 ZoomControls: Modal closed");
            zoomModalAPI = null;
          },
        });

        console.log("🎯 ZoomControls: Modal created, showing...");
        zoomModalAPI.show();
        console.log("🎯 ZoomControls: Modal show() called");
      } catch (error) {
        console.error("🎯 ZoomControls: Failed to create/show modal:", error);
      }
    } else {
      console.warn("🎯 ZoomControls: No dropdown chevron element found");
    }
  };

  /**
   * Handle zoom mode selection from modal
   */
  const handleZoomModeSelect = (mode: ZoomMode): void => {
    if (isDestroyed) return;

    uiState.setZoomMode(mode);
    onZoomAction?.(mode);
    updateFromState();
  };

  /**
   * Handle zoom icon click to execute current zoom mode
   */
  const handleZoomIconClick = (): void => {
    if (isDestroyed) return;

    const state = uiState.getState();
    onZoomAction?.(state.zoom_mode);
  };

  /**
   * Create the dropdown chevron element
   */
  const createDropdownChevron = (): HTMLElement => {
    dropdownChevronAPI = createChevron({
      orientation: "down",
      className: "dropdown-container",
      svgClassName: "dropdown-chevron",
      onClick: handleDropdownClick,
    });
    return dropdownChevronAPI.getElement();
  };

  /**
   * Create the zoom percentage text element
   */
  const createZoomPercentage = (): HTMLElement => {
    const percentageElement = document.createElement("span");
    percentageElement.className = "zoom-percentage";
    updateZoomPercentage(percentageElement);
    return percentageElement;
  };

  /**
   * Update zoom percentage display
   */
  const updateZoomPercentage = (element: HTMLElement): void => {
    const state = uiState.getState();
    const percentage = Math.round(state.zoom_value * 100);
    element.innerHTML = `${percentage}<span class="zoom-percentage-unit">%</span>`;
  };

  /**
   * Initialize the zoom controls with all elements
   */
  const initZoomControls = (): void => {
    try {
      // Create dropdown chevron
      dropdownChevronElement = createDropdownChevron();
      container.appendChild(dropdownChevronElement);

      // Create zoom icon button with initial state
      const state = uiState.getState();
      const iconName = getZoomModeIcon(state.zoom_mode);

      const buttonConfig: any = {
        icon: iconName,
        size: 32,
        iconSize: 24,
        behavior: "button",
        className: "zoom-button",
        onClick: handleZoomIconClick,
      };

      zoomButtonAPI = createIconButton(buttonConfig);
      container.appendChild(zoomButtonAPI.getElement());

      // Create zoom percentage text
      zoomPercentageElement = createZoomPercentage();
      container.appendChild(zoomPercentageElement);
    } catch (error) {
      console.error("Failed to create ZoomControls component:", error);
      throw error;
    }
  };

  /**
   * Update component from UI state
   */
  const updateFromState = (): void => {
    if (isDestroyed) return;

    const state = uiState.getState();

    // Update zoom percentage
    if (zoomPercentageElement) {
      updateZoomPercentage(zoomPercentageElement);
    }

    // Update zoom button icon
    if (zoomButtonAPI) {
      const iconName = getZoomModeIcon(state.zoom_mode);
      zoomButtonAPI.setIcon(iconName);
    }
  };

  /**
   * Clean up resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Destroy zoom modal if open
    zoomModalAPI?.destroy();
    zoomModalAPI = null;

    // Destroy zoom button component
    zoomButtonAPI?.destroy();
    zoomButtonAPI = null;

    // Destroy chevron component
    dropdownChevronAPI?.destroy();
    dropdownChevronAPI = null;

    // Clear references
    zoomPercentageElement = null;
    dropdownChevronElement = null;

    // Remove from DOM if still connected
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };

  // Initialize the component
  initZoomControls();

  return {
    getElement: () => container,
    updateFromState,
    destroy,
  };
};

/**
 * Creates ZoomControls component and appends to container
 * @param container - Container element to append to
 * @param config - Configuration for the zoom controls
 * @returns ZoomControls API
 */
export const createZoomControlsInContainer = (
  container: HTMLElement,
  config: ZoomControlsConfig
): ZoomControlsAPI => {
  const zoomControls = createZoomControls(config);
  container.appendChild(zoomControls.getElement());
  return zoomControls;
};
